import { Component } from '@angular/core';
import { DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { ModalTravelManagementComponent } from '../../../shared/modals/modal-travel-management/modal-travel-management.component';
import { Title } from '@angular/platform-browser';
import { BookingService } from '../../../api/services';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import {
  BookingItem,
  BookingItemPackage,
  BookingItemRoom,
  Payment,
  UserItemEssentials,
} from '../../../api/models';
import { SlugifyPipe } from '../../../shared/pipes/slugify.pipe';
import { enumRoomType } from '../../../shared/utils/enum-room-type';
import { environment } from '../../../../environments/environment';
import { enumPaymentStatus } from '../../../shared/utils/enum-payment-status';
import { ConfirmationService, MessageService } from 'primeng/api';
import { enumPaymentMethod } from '../../../shared/utils/enum-payment-method';
import { enumAssignmentStatus } from '../../../shared/utils/enum-assignment-request';

@Component({
  selector: 'app-travel-management-user',
  templateUrl: './travel-management-user.component.html',
  styleUrl: './travel-management-user.component.scss',
})
export class TravelManagementUserComponent {
  travelManagementRef: DynamicDialogRef | undefined;
  booking_identifier?: string;
  booking?: BookingItem;
  dates: string[] = [];
  environmentMediaUrl = environment.mediaUrl;
  enumPaymentStatus = enumPaymentStatus;
  enumPaymentMethod = enumPaymentMethod;
  travelers: UserItemEssentials[] = [];
  limit = 5;
  skip = 0;
  totalRecords = 0;
  loading_page = false;
  enumAssignmentStatus = enumAssignmentStatus;
  addPaymentModal = false;
  addPaymentAmount = new FormControl(50);
  addPaymentLoading = false;

  constructor(
    public dialogService: DialogService,
    private title: Title,
    private bookingService: BookingService,
    private ar: ActivatedRoute,
    private slugifyPipe: SlugifyPipe,
    private router: Router,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.title.setTitle('SocialTravels - Gestione del viaggio');
    this.ar.params.subscribe((p: Params) => {
      if (p) {
        this.booking_identifier = p['identifier'];
        this.getOneBooking();
      }
    });
  }

  getOneBooking() {
    this.loading_page = true;
    this.bookingService
      .bookingControllerGetBooking({
        booking_identifier: this.booking_identifier!,
      })
      .subscribe({
        next: (res) => {
          this.booking = res.data;
          if (this.booking.travel.funding) {
            this.booking.amount = this.booking.payments.reduce(
              (acc: number, p: Payment) =>
                (acc +=
                  p.amount -
                  (this.booking!.travel.customer_pays_fees
                    ? p.amount - p.amount / 1.03
                    : 0)),
              0,
            );
          }
          this.getTravelers();
          this.dates.push(this.booking.travel.from_date);
          this.dates.push(this.booking.travel.to_date);
          this.loading_page = false;
        },
      });
  }

  openTravelManagementModal() {
    this.travelManagementRef = this.dialogService.open(
      ModalTravelManagementComponent,
      {
        header: 'Dettaglio pratica',
        width: '70%',
        styleClass: 'modal-res',
      },
    );
  }

  goTo() {
    const slugifyName = this.slugifyPipe.transform(this.booking!.travel.name);
    this.router.navigate([
      '/viaggi-di-gruppo',
      this.booking!.travel.countries[0].country.ISO,
      this.booking!.travel.countries[0].country.name,
      this.booking!.travel.id,
      slugifyName,
    ]);
  }

  getRoomHeader(room: BookingItemRoom) {
    let room_type;
    let adults = ' ' + room.n_adults + ' adulti ';
    let children;
    switch (room.room_type) {
      case enumRoomType.SGL:
        room_type = 'Camera singola';
        break;
      case enumRoomType.DBL:
        room_type = 'Camera doppia';
        break;
      case enumRoomType.TRP:
        room_type = 'Camera tripla';
        break;
      case enumRoomType.QUD:
        room_type = 'Camera quadrupla';
        break;
    }

    if (room.n_children != 0) {
      children = room.n_children + room.n_infants + ' bambini ';
    } else {
      children = '';
    }
    return `${room_type} (${adults! + children})`;
  }

  getPackageHeader(p: BookingItemPackage) {
    let package_header = '';
    if (p.travel_package.name) {
      package_header = p.travel_package.name;
    } else {
      package_header = 'Nessun pacchetto';
    }
    return package_header;
  }

  getTravelers() {
    this.bookingService
      .bookingControllerTravelTravelers({
        limit: this.limit,
        skip: this.skip,
        travel_id: this.booking?.travel.id!,
      })
      .subscribe({
        next: (res) => {
          this.travelers = res.data.list;
          this.totalRecords = res.data.count;
        },
      });
  }

  loadMoreTravelers() {
    this.limit += this.limit;
    this.getTravelers();
  }

  addPayment() {
    this.bookingService
      .bookingControllerAddAdvanceForGmg({
        body: { amount: this.addPaymentAmount.value! },
        booking_identifier: this.booking!.identifier,
      })
      .subscribe({
        next: (res) => {
          this.addPaymentLoading = true;
          window.location.href = res.data.redirect_url;
        },
        error: (err) => {
          this.addPaymentLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Creazione versamento',
            detail: 'Impossibile creare il versamento',
          });
        },
      });
  }

  confirmFullPayment(id: string) {
    this.confirmationService.confirm({
      message: 'Confermi di voler saldare il pagamento?',
      header: 'Saldo pagamento',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'bg-dialog p-button-sm',
      rejectButtonStyleClass: 'p-button-outlined p-button-sm mr-2',
      accept: () => {
        this.bookingService
          .bookingControllerCreateStripePayment({ payment_identifier: id })
          .subscribe({
            next: (res) => {
              window.open(res.data.redirect_url);
            },
            error: (err) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Saldo',
                detail: "Impossibile completare l'operazione.",
              });
            },
          });
      },
    });
  }
}
